import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

import { PiHandCoins, PiMoney, PiStar } from 'react-icons/pi'
import BoltSvg from '@/assets/icons/bolt.svg'
import { UberIcon } from '@/components/common/Icon'

import NumberFlow from '@number-flow/react'

import { currencyFormatOptions, formatCurrency } from '@/utils/currency'

import Header from '@/components/dashboard/Header/Header'
import Navigation from '@/components/dashboard/Navigation/Navigation'

import MotionDiv from '@/components/common/Motion/MotionDiv'
import MotionList from '@/components/common/Motion/MotionList'

import incomeAPI, { IncomeItem } from '@/services/api/income'

import { useDashboardStore } from '@/store/useDashboardStore'
import { useTourStore } from '@/store/useTourStore'

import { formatDateGroup } from '@/components/common/Motion/MotionList'
import { getParentCategories } from '@/types/income'
import { usePageSetup } from '@/hooks/usePageSetup'

import IncomeCategoryGrid from '@/components/pages/Income/IncomeCategoryGrid'
import IncomeEmptyState from '@/components/pages/Income/IncomeEmptyState'
import IncomeTour from '@/tours/IncomeTour'
import useDeleteMonth from '@/components/dashboard/DeleteMonthModal/useDeleteMonth'

const getIncomeTitle = (income: IncomeItem, t: TFunction) => {
  return getParentCategories(t).find(category => category.value === income.category)?.label ?? ''
}

const incomeIcon = (income: IncomeItem) => {
  switch (income.category) {
    case 'other':
      return <PiMoney className="h-6 w-6 text-zinc-900" />
    case 'tips':
      return <PiHandCoins className="h-6 w-6 text-zinc-900" />
    case 'bonus':
      return <PiStar className="h-6 w-6 text-zinc-900" />
  }

  switch (income.category) {
    case 'uber_trip':
      return <UberIcon size="lg" />
    case 'uber_bonus':
      return <UberIcon size="lg" />
    case 'uber_tips':
      return <UberIcon size="lg" />
  }

  return <img src={BoltSvg} className="h-12 w-12 text-black" />
}

export const DashboardIncomesPage = () => {
  const { t, i18n } = useTranslation()
  usePageSetup(t('income.pageTitle.list'))

  const navigate = useNavigate();

  const financialYear = useDashboardStore((state) => state.financialYear)

  const incomes = useDashboardStore((state) => state.incomes)
  const setIncomes = useDashboardStore((state) => state.setIncomes)

  useEffect(() => {
    incomeAPI.index({ year: financialYear })
      .then((response) => setIncomes(response.incomes))
  }, [])

  const { modal: DeleteMonthModal, requestMonthDelete } = useDeleteMonth('income')

  const incomeTourCompleted = useTourStore((state) => state.incomeTourCompleted)
  useEffect(() => {
    if (!IncomeTour.isActive() && !incomeTourCompleted) {
      IncomeTour.start()
    }
  }, [incomes, incomeTourCompleted])

  const total = incomes.reduce((acc, income) => acc + income.amount, 0)

  const locale = i18n.language

  const items = incomes.map((income) => ({
    id: income.updated_at + income.amount,
    title: getIncomeTitle(income, t),
    subtitle: income.date,
    value: formatCurrency.format(income.amount),
    icon: incomeIcon(income),
    groupId: formatDateGroup(income.date, locale),
    props: {
      onClick: () => navigate(`/income/update`, { state: { income } })
    }
  }))

  /* TODO: Refactor to one function for expenses and incomes */
  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const isCurrentYear = currentYear === financialYear

  const groups = Array.from({ length: 12 }).map((_, index) => {
    const monthIndex = 11 - index
    const monthDate = new Date(financialYear, monthIndex, 1);
    const monthName = formatDateGroup(monthDate.toISOString(), locale);

    const groupItems = incomes.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate.getMonth() === monthIndex;
    });

    /* The -1 is very important, we do not want incomes for this month */
    /* Because not yet all trips are finished */
    if (
      groupItems.length === 0 &&
      isCurrentYear &&
      monthIndex > currentMonthIndex - 1
    ) {
      return null
    }

    const groupTotal = groupItems.reduce((sum, item) => sum + item.amount, 0);

    return {
      id: monthName,
      name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      description: groupTotal > 0 ? formatCurrency.format(groupTotal) : '',
      open: (11 - index) == currentMonthIndex - 1 && isCurrentYear,
      friction: 0.005,
      childrenFriction: 0.01,
      EmptyGroup: <IncomeEmptyState />,
      onDelete: groupItems.length > 0
        ? () => requestMonthDelete({
          monthIndex,
          name: monthName.charAt(0).toUpperCase() + monthName.slice(1)
        })
        : undefined,
      props: {
        className: 'border-b border-zinc-200 pb-4'
      }
    }
  }).filter((group) => group !== null);

  return (
    <div className="h-full bg-white flex flex-col">
      {DeleteMonthModal}
      <Header />
      <div className="flex-1 p-5 pt-2 pb-[10rem]">
        <MotionDiv className='mb-5'>
          <p className='text-zinc-700 text-sm'>{t('income.yourIncome')}</p>
          <NumberFlow
            format={{
              style: currencyFormatOptions.style,
              currency: currencyFormatOptions.currency,
              useGrouping: currencyFormatOptions.useGrouping
            }}
            className='text-[1.75rem] font-semibold text-zinc-900'
            value={total}
          />
        </MotionDiv>
        <MotionDiv className='mb-6'>
          <IncomeCategoryGrid />
        </MotionDiv>
        <MotionList
          items={items}
          groups={groups}
          className='tour-income-list'
        />
      </div>
      <Navigation />
    </div>
  )
}

export default DashboardIncomesPage